import type {
  AdminUser,
  KabelContent,
  ListResponse,
  MediaAsset,
  MediaKind,
} from "./types";
import {
  CONTENT_LANGUAGE_HEADER,
  getContentLanguage,
} from "./i18n/content-language";

export const API_BASE_URL = (
  import.meta.env.VITE_API_BASE_URL ?? "http://localhost:4100/api/v1"
).replace(/\/$/, "");

const TOKEN_KEY = "kabel_admin_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
};

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const token = getToken();
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  headers.set(CONTENT_LANGUAGE_HEADER, getContentLanguage());
  if (options.body !== undefined) headers.set("Content-Type", "application/json");

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    body:
      options.body === undefined ? undefined : JSON.stringify(options.body),
  });

  if (response.status === 401) {
    setToken(null);
    if (window.location.pathname !== "/login") window.location.assign("/login");
  }

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(payload?.message ?? `请求失败（${response.status}）`);
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function login(username: string, password: string) {
  return apiRequest<{ accessToken: string; admin: AdminUser }>("/auth/login", {
    method: "POST",
    body: { username, password },
  });
}

export function getPage<T = Record<string, unknown>>(key: string) {
  return apiRequest<KabelContent<T>>(`/admin/kabel/pages/${key}`);
}

export function savePage<T>(key: string, title: string, data: T) {
  return apiRequest<KabelContent<T>>(`/admin/kabel/pages/${key}`, {
    method: "PUT",
    body: { title, data },
  });
}

export function listContent<T = Record<string, unknown>>(
  collection: string,
  query = "",
) {
  return apiRequest<ListResponse<KabelContent<T>>>(
    `/admin/kabel/${collection}${query ? `?${query}` : ""}`,
  );
}

export function getContent<T = Record<string, unknown>>(
  collection: string,
  id: string,
) {
  return apiRequest<KabelContent<T>>(`/admin/kabel/${collection}/${id}`);
}

export function createContent<T>(collection: string, body: T) {
  return apiRequest<KabelContent>(`/admin/kabel/${collection}`, {
    method: "POST",
    body,
  });
}

export function updateContent<T>(collection: string, id: string, body: T) {
  return apiRequest<KabelContent>(`/admin/kabel/${collection}/${id}`, {
    method: "PATCH",
    body,
  });
}

export function deleteContent(collection: string, id: string) {
  return apiRequest<{ success: true }>(`/admin/kabel/${collection}/${id}`, {
    method: "DELETE",
  });
}

type UploadSession = {
  sessionId: string;
  mode: "DIRECT";
  uploadUrl: string;
  method: "POST";
  formFields: Record<string, string>;
};

export function inferMediaKind(file: File): MediaKind {
  const extension = file.name.split(".").pop()?.toLowerCase();
  if (
    file.type.startsWith("image/") ||
    ["jpg", "jpeg", "png", "webp", "gif", "svg"].includes(extension ?? "")
  ) return "image";
  if (
    file.type.startsWith("video/") ||
    ["mp4", "webm", "mov"].includes(extension ?? "")
  ) return "video";
  throw new Error("媒体库仅支持上传图片和视频");
}

export async function uploadMedia(file: File, requestedKind?: MediaKind) {
  const inferredKind = inferMediaKind(file);
  if (requestedKind && requestedKind !== inferredKind) {
    throw new Error(
      requestedKind === "image" ? "请选择图片文件" : "请选择视频文件",
    );
  }
  const kind = requestedKind ?? inferredKind;
  const session = await apiRequest<UploadSession>(
    "/admin/media/upload-sessions",
    {
      method: "POST",
      body: {
        originalName: file.name,
        mimeType: file.type || "application/octet-stream",
        sizeBytes: file.size,
        kind,
      },
    },
  );
  const form = new FormData();
  Object.entries(session.formFields).forEach(([key, value]) => {
    form.append(key, value);
  });
  form.append("file", file);
  const response = await fetch(session.uploadUrl, {
    method: session.method,
    body: form,
  });
  if (!response.ok) {
    throw new Error(`文件直传 OSS 失败（${response.status}）`);
  }
  return apiRequest<MediaAsset>(
    `/admin/media/upload-sessions/${session.sessionId}/complete`,
    { method: "POST", body: {} },
  );
}

export function resolveImageUrl(value?: string | null) {
  return value ?? "";
}
